import React, { useState, useEffect } from 'react';
import { COLORS } from '../config/constants';
import api from '../config/api';
import { shipmentService, projectService, mailService } from '../services/api.service';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import SendMailModal from '../components/SendMailModal';

const ShipmentList = ({ user }) => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingShipment, setEditingShipment] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showMailModal, setShowMailModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    project: '',
    source_location: '',
    destination: '',
    date_from: '',
    date_to: ''
  });
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    loadShipments();
  }, []);

  const loadShipments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/shipment');
      setShipments(response.data);
    } catch (error) {
      console.error('Sevkiyat kayıtları yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (shipment) => {
    setEditingShipment({
      ...shipment,
      destination: shipment.destination || '',
      invoice_no: shipment.invoice_no || '',
      total_weight: shipment.total_weight || '',
      notes: shipment.notes || ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/shipment/${editingShipment.id}`, editingShipment);
      setMessage({ type: 'success', text: 'Sevkiyat kaydı güncellendi!' });
      setShowEditModal(false);
      loadShipments();
    } catch (error) {
      setMessage({ type: 'error', text: 'Güncelleme başarısız!' });
    }
  };

  const handleDeleteClick = (shipment) => {
    setItemToDelete(shipment);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    
    try {
      await api.delete(`/shipment/${itemToDelete.id}`);
      setMessage({ type: 'success', text: 'Sevkiyat kaydı silindi!' });
      loadShipments();
    } catch (error) {
      setMessage({ type: 'error', text: 'Silme işlemi başarısız!' });
    } finally {
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      setMessage({ type: 'success', text: 'Excel dosyası indiriliyor...' });
      await shipmentService.exportExcel(filters);
      setMessage({ type: 'success', text: 'Excel dosyası başarıyla indirildi!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Excel export başarısız!' });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      setMessage({ type: 'success', text: 'PDF dosyası indiriliyor...' });
      await shipmentService.exportPDF(filters);
      setMessage({ type: 'success', text: 'PDF dosyası başarıyla indirildi!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'PDF export başarısız!' });
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setMessage({ type: 'error', text: 'Lütfen Excel dosyası (.xlsx veya .xls) seçin!' });
      return;
    }

    try {
      setImporting(true);
      setMessage({ type: 'success', text: 'Excel dosyası yükleniyor...' });
      
      const result = await shipmentService.importExcel(file);
      
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: result.message || `Başarılı! ${result.imported} kayıt eklendi.`
        });
      } else {
        setMessage({ type: 'error', text: result.message || 'Import başarısız!' });
      }
      
      // Reload data
      loadShipments();
    } catch (error) {
      console.error('Import error:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Excel import başarısız!' });
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const canEdit = user?.role === 'admin' || user?.role === 'operator';

  const clearFilters = () => {
    setFilters({
      project: '',
      source_location: '',
      destination: '',
      date_from: '',
      date_to: ''
    });
  };

  const handleSendMail = async (recipients, attachExcel, attachPdf) => {
    try {
      await mailService.sendShipmentMail(recipients, attachExcel, attachPdf, filters);
      setMessage({ type: 'success', text: `Mail başarıyla ${recipients.length} alıcıya gönderildi!` });
    } catch (error) {
      console.error('Mail gönderme hatası:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Mail gönderilemedi!' });
    }
  };

  const filteredShipments = shipments.filter(shipment => {
    const matchesSearch = 
      shipment.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.part_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.invoice_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shipment.material_type?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesProject = !filters.project || shipment.project_name?.toLowerCase().includes(filters.project.toLowerCase());
    const matchesSourceLocation = !filters.source_location || shipment.source_location?.toLowerCase().includes(filters.source_location.toLowerCase());
    const matchesDestination = !filters.destination || shipment.destination?.toLowerCase().includes(filters.destination.toLowerCase());
    
    let matchesDateFrom = true;
    let matchesDateTo = true;
    
    if (filters.date_from && shipment.shipment_date) {
      matchesDateFrom = new Date(shipment.shipment_date) >= new Date(filters.date_from);
    }
    
    if (filters.date_to && shipment.shipment_date) {
      matchesDateTo = new Date(shipment.shipment_date) <= new Date(filters.date_to);
    }
    
    return matchesSearch && matchesProject && matchesSourceLocation && matchesDestination && matchesDateFrom && matchesDateTo;
  });

  // Get unique values from FILTERED shipments (only visible records)
  const uniqueProjects = [...new Set(filteredShipments.map(s => s.project_name).filter(Boolean))];
  const uniqueSourceLocations = [...new Set(filteredShipments.map(s => s.source_location).filter(Boolean))];
  const uniqueDestinations = [...new Set(filteredShipments.map(s => s.destination).filter(Boolean))];

  return (
    <div style={{ 
      padding: '2rem',
      background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
      minHeight: '100vh'
    }}>
      {/* Modern Card Container */}
      <div style={{
        background: '#FFFFFF',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden',
        border: '1px solid rgba(228, 10, 4, 0.1)'
      }}>
        {/* Modern Header with Gradient */}
        <div style={{
          background: 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)',
          padding: '2rem',
          color: '#FFFFFF'
        }}>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', margin: 0, letterSpacing: '-0.5px' }}>
            🚚 Sevkiyat Kayıtları (Lojistik)
          </h1>
          <p style={{ fontSize: '1rem', opacity: 0.9, marginTop: '0.5rem' }}>
            Tüm lojistik ve sevkiyat işlemlerini yönetin
          </p>
          
          {/* Stats Bar */}
          <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.25rem' }}>Toplam Sevkiyat</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{shipments.length}</div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.25rem' }}>Toplam Ağırlık</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                {shipments.reduce((sum, s) => sum + (parseFloat(s.total_weight) || 0), 0).toFixed(0)} kg
              </div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.25rem' }}>Filtreli Kayıt</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{filteredShipments.length}</div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.25rem' }}>Filtreli Ağırlık</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                {filteredShipments.reduce((sum, s) => sum + (parseFloat(s.total_weight) || 0), 0).toFixed(0)} kg
              </div>
            </div>
          </div>
        </div>

        {/* Controls Section - Modern */}
        <div style={{
          padding: '1.5rem 2rem',
          background: '#FFFFFF',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
          borderBottom: '1px solid #E8E8E8'
        }}>
          <div style={{ flex: '1', maxWidth: '500px', position: 'relative' }}>
            <span style={{
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '1.2rem',
              color: '#999'
            }}>🔍</span>
            <input
              type="text"
              placeholder="Proje, parça, malzeme türü veya sevk yerinde ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.875rem 1rem 0.875rem 3rem',
                border: '2px solid #E8E8E8',
                borderRadius: '10px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'all 0.3s ease'
              }}
              onFocus={(e) => {
                e.target.style.border = '2px solid #E40A04';
                e.target.style.boxShadow = '0 4px 12px rgba(228, 10, 4, 0.15)';
              }}
              onBlur={(e) => {
                e.target.style.border = '2px solid #E8E8E8';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          
          {/* Export Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                padding: '0.875rem 1.75rem',
                background: showFilters ? 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)' : '#FFFFFF',
                color: showFilters ? '#FFFFFF' : '#333333',
                border: showFilters ? 'none' : '2px solid #E40A04',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: showFilters 
                  ? '0 4px 12px rgba(228, 10, 4, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              🔍 Filtreler
            </button>
            <button
              onClick={handleImportClick}
              disabled={importing}
              style={{
                padding: '0.875rem 1.75rem',
                background: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: importing ? 'not-allowed' : 'pointer',
                opacity: importing ? 0.5 : 1,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              📥 Excel Yükle
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <button
              onClick={handleExportExcel}
              disabled={exporting || filteredShipments.length === 0}
              style={{
                padding: '0.875rem 1.75rem',
                background: '#10B981',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: exporting || filteredShipments.length === 0 ? 'not-allowed' : 'pointer',
                opacity: exporting || filteredShipments.length === 0 ? 0.5 : 1,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              📊 Excel İndir
            </button>
            <button
              onClick={handleExportPDF}
              disabled={exporting || filteredShipments.length === 0}
              style={{
                padding: '0.875rem 1.75rem',
                background: 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: exporting || filteredShipments.length === 0 ? 'not-allowed' : 'pointer',
                opacity: exporting || filteredShipments.length === 0 ? 0.5 : 1,
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(228, 10, 4, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              📄 PDF İndir
            </button>
            <button
              onClick={() => setShowMailModal(true)}
              style={{
                padding: '0.875rem 1.75rem',
                background: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              📧 Mail Gönder
            </button>
          </div>
        </div>

        {/* Message */}
        {message.text && (
          <div style={{
            padding: '1rem',
            margin: '1.5rem 2rem',
            borderRadius: '8px',
            background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
            color: message.type === 'success' ? '#065f46' : '#991b1b'
          }}>
            {message.text}
          </div>
        )}

        {/* Filters Panel */}
        {showFilters && (
          <div style={{ 
            background: COLORS.white, 
            padding: '1.5rem 2rem',
            borderTop: '1px solid #E8E8E8'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.1rem', color: COLORS.text }}>
              Filtreleme Seçenekleri
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                  Proje Adı
                </label>
                <input
                  type="text"
                  value={filters.project}
                  list="project-names-list-ship"
                  onChange={(e) => setFilters({...filters, project: e.target.value})}
                  placeholder="Liste seç veya yaz..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none'
                  }}
                />
                <datalist id="project-names-list-ship">
                  {uniqueProjects.map(proj => (
                    <option key={proj} value={proj} />
                  ))}
                </datalist>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                  Çıkış Yeri
                </label>
                <input
                  type="text"
                  list="source-locations-list"
                  value={filters.source_location}
                  onChange={(e) => setFilters({...filters, source_location: e.target.value})}
                  placeholder="Liste seç veya yaz..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none'
                  }}
                />
                <datalist id="source-locations-list">
                  {uniqueSourceLocations.map(loc => (
                    <option key={loc} value={loc} />
                  ))}
                </datalist>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                  Sevk Yeri
                </label>
                <input
                  type="text"
                  list="destinations-list"
                  value={filters.destination}
                  onChange={(e) => setFilters({...filters, destination: e.target.value})}
                  placeholder="Liste seç veya yaz..."
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none'
                  }}
                />
                <datalist id="destinations-list">
                  {uniqueDestinations.map(dest => (
                    <option key={dest} value={dest} />
                  ))}
                </datalist>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                  Başlangıç Tarihi
                </label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '500' }}>
                  Bitiş Tarihi
                </label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${COLORS.background}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
            
            <div style={{ marginTop: '1rem' }}>
              <button
                onClick={clearFilters}
                style={{
                  padding: '0.5rem 1rem',
                  background: COLORS.background,
                  color: COLORS.text,
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Filtreleri Temizle
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div style={{ 
          padding: '0 2rem 2rem 2rem'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p style={{ color: COLORS.textLight }}>Yükleniyor...</p>
            </div>
          ) : filteredShipments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p style={{ color: COLORS.textLight }}>Henüz sevkiyat kaydı yok</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: COLORS.background }}>
                <th style={tableHeaderStyle}>Proje</th>
                <th style={tableHeaderStyle}>Parça Adı</th>
                <th style={tableHeaderStyle}>Malzeme Türü</th>
                <th style={tableHeaderStyle}>Uzunluk</th>
                <th style={tableHeaderStyle}>Parça Adedi</th>
                <th style={tableHeaderStyle}>Ağırlık (kg)</th>
                <th style={tableHeaderStyle}>Tarih</th>
                <th style={tableHeaderStyle}>Çıkış Yeri</th>
                <th style={tableHeaderStyle}>Sevk Yeri</th>
                <th style={tableHeaderStyle}>İrsaliye No</th>
                {canEdit && <th style={tableHeaderStyle}>İşlemler</th>}
              </tr>
            </thead>
            <tbody>
              {filteredShipments.map((shipment, index) => (
                <tr key={shipment.id || index} style={{ borderBottom: `1px solid ${COLORS.background}` }}>
                  <td style={{...tableCellStyle, fontWeight: '600'}}>{shipment.project_name || '-'}</td>
                  <td style={tableCellStyle}>{shipment.part_name || '-'}</td>
                  <td style={tableCellStyle}>{shipment.material_type || '-'}</td>
                  <td style={{...tableCellStyle, textAlign: 'center'}}>{shipment.length || '-'}</td>
                  <td style={{...tableCellStyle, textAlign: 'center', fontWeight: '600'}}>{shipment.quantity || '-'}</td>
                  <td style={{...tableCellStyle, textAlign: 'center', fontWeight: '700', color: COLORS.primary}}>
                    {shipment.total_weight ? `${parseFloat(shipment.total_weight).toFixed(2)} kg` : '-'}
                  </td>
                  <td style={tableCellStyle}>
                    {shipment.shipment_date ? new Date(shipment.shipment_date).toLocaleDateString('tr-TR') : '-'}
                  </td>
                  <td style={tableCellStyle}>{shipment.source_location || '-'}</td>
                  <td style={tableCellStyle}>{shipment.destination || '-'}</td>
                  <td style={tableCellStyle}>{shipment.invoice_no || '-'}</td>
                  {canEdit && (
                    <td style={tableCellStyle}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleEdit(shipment)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#3b82f6',
                            color: COLORS.white,
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            cursor: 'pointer'
                          }}
                        >
                          Düzenle
                        </button>
                        {user?.role === 'admin' && (
                          <button
                            onClick={() => handleDeleteClick(shipment)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: COLORS.primary,
                              color: COLORS.white,
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer'
                            }}
                          >
                            Sil
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>

      </div> {/* Close Modern Card */}

      {/* Edit Modal */}
      {showEditModal && editingShipment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: COLORS.white,
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ marginBottom: '1.5rem' }}>Sevkiyat Kaydını Düzenle</h2>
            <form onSubmit={handleUpdate}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <FormField label="Sevk Yeri" value={editingShipment.destination} 
                  onChange={(v) => setEditingShipment({...editingShipment, destination: v})} required />
                <FormField label="İrsaliye No" value={editingShipment.invoice_no}
                  onChange={(v) => setEditingShipment({...editingShipment, invoice_no: v})} />
                <FormField label="Toplam Ağırlık (kg)" type="number" step="0.01" value={editingShipment.total_weight}
                  onChange={(v) => setEditingShipment({...editingShipment, total_weight: v})} />
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Notlar</label>
                  <textarea
                    value={editingShipment.notes}
                    onChange={(e) => setEditingShipment({...editingShipment, notes: e.target.value})}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `1px solid ${COLORS.background}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button type="submit" style={{
                  padding: '0.75rem 1.5rem',
                  background: COLORS.primary,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}>
                  Kaydet
                </button>
                <button type="button" onClick={() => setShowEditModal(false)} style={{
                  padding: '0.75rem 1.5rem',
                  background: COLORS.background,
                  color: COLORS.text,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}>
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemType="sevkiyat kaydı"
      />

      <SendMailModal
        isOpen={showMailModal}
        onClose={() => setShowMailModal(false)}
        onSend={handleSendMail}
        moduleName="Sevkiyat"
      />
    </div>
  );
};

const FormField = ({ label, value, onChange, type = 'text', step, required = false }) => (
  <div>
    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
      {label} {required && <span style={{ color: COLORS.primary }}>*</span>}
    </label>
    <input
      type={type}
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      style={{
        width: '100%',
        padding: '0.75rem',
        border: `1px solid ${COLORS.background}`,
        borderRadius: '8px',
        fontSize: '1rem'
      }}
    />
  </div>
);

const tableHeaderStyle = {
  padding: '1rem',
  textAlign: 'left',
  fontSize: '0.875rem',
  fontWeight: '600',
  textTransform: 'uppercase'
};

const tableCellStyle = {
  padding: '1rem',
  fontSize: '0.95rem'
};

export default ShipmentList;
